import { eventPhase, EventTimes } from './event-edit-lock'

/**
 * Adding a one-time event to an existing series ("Pridať do série", docs/events.md, _Adding an event to a series_).
 * Only the link is written: the termín keeps its name, settings, date and time, and nobody is told.
 */

/**
 * How a series is named to managers: its series name, or - for a series created before it had one (`''`) - the name
 * of its termíny. `ManagedEventView` states the same rule in SQL (docs/events.md, _Series name_).
 */
export function seriesDisplayName(series: { seriesName: string; name: string }): string {
  return series.seriesName.trim() || series.name
}

/** What the rule reads of the event: its times, whether it is in a series already, and whether it was called off. */
export interface SeriesJoinEvent extends EventTimes {
  blueprint: unknown
  cancelledAt: Date | string | null
}

/** Why a termín cannot join a series, worded as the API's refusal. */
export type SeriesJoinRefusal =
  | 'event.alreadyInSeries'
  | 'event.cancelled'
  | 'event.started'
  | 'event.ended'
  | 'event.activityMismatch'
  | 'event.seriesEnded'

/**
 * What the event alone decides: it must be one-time, not cancelled, and upcoming - as for a series edit, a termín under
 * way or over is never rewritten. `null` when it may join some series.
 */
export function seriesJoinEventRefusal(event: SeriesJoinEvent, now: Date, timezone: string): SeriesJoinRefusal | null {
  if (event.blueprint) {
    return 'event.alreadyInSeries'
  }
  if (event.cancelledAt) {
    return 'event.cancelled'
  }
  const phase = eventPhase(event, now, timezone)
  if (phase === 'ongoing') {
    return 'event.started'
  }
  return phase === 'upcoming' ? null : 'event.ended'
}

/** Whether the detail page offers "Pridať do série": decided from the event alone, the series are loaded on open. */
export function canJoinSeries(event: SeriesJoinEvent, now: Date, timezone: string): boolean {
  return seriesJoinEventRefusal(event, now, timezone) === null
}

/**
 * Whether the event may join this series. The series must be **active** - at least one upcoming, not cancelled termín
 * (`upcoming`) - and have the event's activity. The series' own activity counts, not its termíny': a single edit can
 * change one termín's activity. An event or a series without an activity matches nothing.
 */
export function seriesJoinRefusal(
  event: SeriesJoinEvent & { activityId: string | null },
  series: { activityId: string | null; upcoming: number },
  now: Date,
  timezone: string,
): SeriesJoinRefusal | null {
  const refusal = seriesJoinEventRefusal(event, now, timezone)
  if (refusal) {
    return refusal
  }
  if (!event.activityId || event.activityId !== series.activityId) {
    return 'event.activityMismatch'
  }
  return series.upcoming > 0 ? null : 'event.seriesEnded'
}
